import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query"
import { useApi } from "./api"
import { Country, State } from "./data"
import { useMemo } from "react"

export enum CustomerAddressType {
  BILLING = "BILLING",
  SHIPPING = "SHIPPING",
}

export interface CustomerAddress {
  id?: string;
  type: CustomerAddressType;
  address: string;
  addressExtra?: string;
  city: string;
  postalCode: string;
  countryId: string;
  stateId?: string;
}

export interface Customer {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  organizationName: string;
  addresses: CustomerAddress[];
  taxId: string;
  createdAt?: string;
  updatedAt?: string;
}

export function createEmptyCustomer(): Customer {
  return {
    id: "",
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    organizationName: "",
    addresses: [],
    taxId: "",
  }
}

export interface CustomerGet extends Customer {
  addresses: (CustomerAddress & { state?: State; country: Country })[];
}

export function useCustomers(pageSize = 10) {
  const { get } = useApi()

  const query = useInfiniteQuery({
    queryKey: ["customers"],
    queryFn: ({ pageParam = 0 }) => get(`customers?skip=${pageParam}&take=10`),
    initialPageParam: 0,
    getNextPageParam: (lastPage: CustomerGet[], allPages) => {
      if (lastPage.length < pageSize) return undefined
      return allPages.length * pageSize
    },
  })

  return {
    customers: query.data?.pages.flat(),
    hasMore: query.hasNextPage,
    isLoading: query.isLoading,
    error: query.error,
    fetchNextPage: query.fetchNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    // Backward compatibility
    size: query.data?.pages.length || 0,
    setSize: (newSize: number) => {
      if (newSize > (query.data?.pages.length || 0)) {
        query.fetchNextPage()
      }
    },
    mutate: query.refetch,
  }
}

export function useCustomer(id: string) {
  const { get } = useApi()

  const query = useQuery({
    queryKey: ["customer", id],
    queryFn: () => get(`customers/${id}`),
  })

  return {
    customer: query.data,
    isLoading: query.isLoading,
    error: query.error,
    mutate: query.refetch,
  }
}

export function useCustomersActions() {
  const { remove, post, put, get } = useApi()
  const queryClient = useQueryClient()

  return useMemo(
    () => ({
      remove: async (id: string) => {
        await remove(`customers/${id}`)
        queryClient.invalidateQueries({ queryKey: ["customers"] })
      },
      create: async (customer: Partial<Customer>): Promise<Customer> => {
        const createdCustomer = await post("customers", customer)
        queryClient.invalidateQueries({ queryKey: ["customers"] })
        return createdCustomer
      },
      update: async (id: string, customer: Partial<Customer>) => {
        const updatedCustomer = await put(`customers/${id}`, customer)
        queryClient.invalidateQueries({ queryKey: ["customer", id] })
        return updatedCustomer
      },
      search: async (search: string): Promise<CustomerGet[]> => {
        return get(`customers?skip=0&take=50&search=${encodeURI(search)}`)
      },
      get: async (id: string): Promise<CustomerGet> => {
        return get(`customers/${id}`)
      },
    }),
    [remove, post, put, get, queryClient],
  )
}
